'use client'

import {
    createContext,
    type DetailedHTMLProps,
    type DetailsHTMLAttributes,
    type ReactElement,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react'

import cn from 'clsx'
import { Heading } from '@/components/Text/Heading'
import { Button } from '@/components/Button/Button'
import { Text } from '@/components/Text/Text'
import { ThemeContext } from '@/utils/themeContext'
import { Plus } from '@doist/icons/brand'
import css from './Accordion.module.css'

type AccordionItemType = string | ReactElement

export class AccordionItem {
    question: AccordionItemType
    answer: AccordionItemType

    constructor(question: AccordionItemType, answer: AccordionItemType) {
        this.question = question
        this.answer = answer
    }
}

export type AccordionProps = {
    items: AccordionItem[]
    title?: string
    caption?: ReactNode
    useAsTemplate?: boolean
    answerContainerClassName?: string
    className?: string
    defaultIndex?: number
    onOpenItem?: (index: number, element: HTMLElement | null) => void
    onCloseItem?: (index: number) => void
}

type AccordionContextProps = {
    open: number
}

const AccordionContext = createContext<AccordionContextProps>({ open: -1 })

/**
 * An Accordion styled component.
 */
export function Accordion({
    title,
    items = [],
    caption,
    useAsTemplate = false,
    answerContainerClassName,
    className,
    defaultIndex = -1,
    onOpenItem,
    onCloseItem,
}: AccordionProps) {
    const { theme = 'todoist' } = useContext(ThemeContext)
    const [openIndex, setOpenIndex] = useState(defaultIndex)
    const accordionId = useId()

    const accordionItemClickHandler = useCallback(
        (index: number, el: HTMLElement) => {
            const isClosingOnly = index === openIndex
            const newIndex = isClosingOnly ? -1 : index

            if (!isClosingOnly && onOpenItem) {
                onOpenItem(newIndex, el)
            }

            if (onCloseItem && openIndex > -1) {
                onCloseItem(openIndex)
            }

            setOpenIndex(newIndex)
        },
        [onCloseItem, onOpenItem, openIndex],
    )

    return (
        <AccordionContext.Provider value={{ open: openIndex }}>
            <div className={cn(css[theme], className)}>
                {title ? (
                    <Heading level="3" className={css.title} size="major-base">
                        {title}
                    </Heading>
                ) : null}

                <div>
                    {items.map((qa, index) => (
                        <AccordionElement
                            // biome-ignore lint/suspicious/noArrayIndexKey: adding real keys would require changes in landing pages
                            key={`${accordionId}_${index}`}
                            item={qa}
                            index={index}
                            useAsTemplate={useAsTemplate}
                            onItemClick={accordionItemClickHandler}
                            answerContainerClassName={answerContainerClassName}
                        />
                    ))}
                </div>

                {caption ? <div className={css.caption}>{caption}</div> : null}
            </div>
        </AccordionContext.Provider>
    )
}

interface AccordionElementProps
    extends DetailedHTMLProps<DetailsHTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    index: number
    item: AccordionItem
    useAsTemplate: boolean
    answerContainerClassName?: string
    onItemClick: (index: number, el: HTMLElement) => void
}

function AccordionElement({
    index,
    item,
    useAsTemplate,
    answerContainerClassName,
    onItemClick,
    ...rest
}: AccordionElementProps) {
    const { open } = useContext(AccordionContext)
    const answerRef = useRef<HTMLDivElement>(null)
    const buttonId = useId()
    const sectionId = useId()

    useEffect(() => {
        // Prevent focusing on closed section content elements (e.g. links)
        if (answerRef.current) {
            if (open !== index) {
                answerRef.current.setAttribute('inert', '')
            } else {
                answerRef.current.removeAttribute('inert')
            }
        }
    }, [open, index])

    const isTwist = useContext(ThemeContext).theme === 'twist'
    const questionTextSize = isTwist ? 'minor-base' : 'minor-lg'
    const questionTextWeight = isTwist ? undefined : 'semibold'
    const answerTextSize = isTwist ? 'body-xs' : 'body-base'

    return (
        <div
            className={cn([{ [css.isOpen]: open === index }, css.item])}
            open={open === index}
            {...rest}
        >
            <WrappedButton useAsTemplate={useAsTemplate}>
                <Button
                    variant="quaternary"
                    size="large"
                    bleed="inline"
                    className={css.itemTitle}
                    onClick={(evt) => onItemClick(index, evt.currentTarget)}
                    id={buttonId}
                    aria-expanded={open === index}
                    aria-controls={sectionId}
                    endIcon={Plus}
                >
                    {useAsTemplate ? (
                        item.question
                    ) : (
                        <Text tag="span" size={questionTextSize} weight={questionTextWeight}>
                            {item.question}
                        </Text>
                    )}
                </Button>
            </WrappedButton>

            <div
                ref={answerRef}
                className={cn(css.answer, answerContainerClassName)}
                role="region"
                id={sectionId}
                aria-labelledby={buttonId}
            >
                <div className={css.answerInner}>
                    {useAsTemplate ? (
                        <div>{item.answer}</div>
                    ) : (
                        <Text size={answerTextSize}>{item.answer}</Text>
                    )}
                </div>
            </div>
        </div>
    )
}

type WrappedButtonProps = { useAsTemplate: boolean; children: ReactNode }

function WrappedButton({ useAsTemplate, children }: WrappedButtonProps) {
    return useAsTemplate ? <>{children}</> : <h4>{children}</h4>
}
